import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { StyleSheet, View, Image, Platform } from 'react-native';
import { Text } from '@/components/Text';
import { useAudioContext } from '@/contexts/AudioContext/useAudioContext';
import { useResponsiveFontSize } from '@/components/Util/reactResponsiveDimensions';
import Slider from '@react-native-community/slider';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { ActivityIndicator } from 'react-native';
import colors from '@/config/colors';

interface AudioPlayerProps {
  source: string;
  autoPlay: boolean;
  isMiniPlayer: boolean;
  isDisabled: boolean;
  isTracked: boolean;
  hasMoveBack: boolean;
  uniqueKey: string;
  playbackRate?: number;
}

interface AudioPlayerRef {
  pausePlayer: () => void;
  stopPlayer: () => Promise<void>;
}

const AudioPlayerComponent = (
  {
    source,
    autoPlay,
    isMiniPlayer,
    isDisabled,
    isTracked,
    hasMoveBack,
    uniqueKey,
    playbackRate,
  }: AudioPlayerProps,
  ref: React.Ref<AudioPlayerRef>,
) => {
  const {
    context,
    resetContext,
    init,
    loadAndPlaySound,
    pauseSound,
    stopSound,
    changeAudioPosition,
    moveBack,
    unloadSound,
  } = useAudioContext();
  const {
    source: activeSource,
    isPlaying,
    duration,
    position,
    isLoading,
  } = context;

  const isActiveAudio = source === activeSource;
  const hasValue =
    isActiveAudio && duration > 0 && isFinite(position) && isFinite(duration);
  const value = hasValue ? Math.min(Math.max(position / duration, 0), 1) : 0;
  const isPlayingAudio = isActiveAudio ? isPlaying : false;

  const controlButtonsSize = useResponsiveFontSize(3);
  const miniControlButtonsSize = useResponsiveFontSize(2.5);

  const onPlay = () => {
    loadAndPlaySound(source, uniqueKey, playbackRate);
  };

  const onSlidingStart = () => {
    pauseSound();
  };

  const onSliderValueChange = (value: number) => {
    changeAudioPosition(value);
  };

  // Format seconds to a string "mm:ss"
  const formatTime = (millis: number) => {
    if (!isActiveAudio) {
      return '00:00';
    } else {
      const seconds = Math.floor(millis / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainderSeconds = seconds % 60;
      const paddedSeconds = remainderSeconds.toString().padStart(2, '0');
      return `${minutes}:${paddedSeconds}`;
    }
  };

  useImperativeHandle(ref, () => {
    return {
      pausePlayer: () => pauseSound(),
      stopPlayer: stopSound,
    };
  }, [pauseSound, stopSound]);

  useEffect(() => {
    (async () => {
      await init(source, autoPlay, isTracked, uniqueKey, playbackRate);
    })();
    return () => {
      stopSound();
    };
  }, [source, autoPlay]);

  useEffect(() => {
    return () => {
      resetContext();
      unloadSound();
    };
  }, []);
  const responsiveStyles = StyleSheet.create({
    responsiveFormattedTime: {
      fontSize: useResponsiveFontSize(1.5),
    },
    customStyles: { opacity: !isPlayingAudio ? 0.4 : 1, marginRight: 20 },
    miniControls: {
      flex: 1,
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2,
      backgroundColor: '#5B79EB',
      borderRadius: 10,
    },
    image: {
      height: controlButtonsSize,
      width: controlButtonsSize,
      resizeMode: 'contain',
    },
  });

  if (isMiniPlayer) {
    return (
      <>
        {isPlayingAudio ? (
          <PressableNeolexon
            disabled={isDisabled}
            style={[
              responsiveStyles.miniControls,
              isDisabled && styles.disabledStyle,
            ]}
            onPress={stopSound}
          >
            <FontAwesome
              name="pause"
              size={miniControlButtonsSize}
              color={isDisabled ? colors.twGrey : 'white'}
            />
          </PressableNeolexon>
        ) : (
          <PressableNeolexon
            disabled={isDisabled}
            style={[
              responsiveStyles.miniControls,
              isDisabled && styles.disabledStyle,
            ]}
            onPress={onPlay}
          >
            <FontAwesome
              name="play"
              size={miniControlButtonsSize}
              color={isDisabled ? colors.twGrey : 'white'}
            />
          </PressableNeolexon>
        )}
      </>
    );
  }

  return (
    <View style={styles.container}>
      {hasMoveBack ? (
        <PressableNeolexon
          disabled={!isPlayingAudio}
          onPress={moveBack}
          style={responsiveStyles.customStyles}
        >
          <Image
            style={responsiveStyles.image}
            source={require('../../assets/rewind15seconds.png')}
          />
        </PressableNeolexon>
      ) : null}
      <View style={[styles.controls, isLoading && styles.controls_disabled]}>
        {isPlayingAudio ? (
          <PressableNeolexon
            disabled={isLoading || isDisabled}
            onPress={() => pauseSound()}
          >
            <FontAwesome
              name="pause"
              size={controlButtonsSize}
              color={isDisabled ? colors.twGrey : 'white'}
            />
          </PressableNeolexon>
        ) : (
          <PressableNeolexon disabled={isDisabled} onPress={onPlay}>
            <FontAwesome
              name="play"
              size={controlButtonsSize}
              color={isDisabled ? colors.twGrey : 'white'}
            />
          </PressableNeolexon>
        )}
      </View>

      {isLoading ? (
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <ActivityIndicator color="#FFFF" size="large" />
        </View>
      ) : (
        <Slider
          key={`slider-${source}-${uniqueKey}`}
          disabled={isDisabled}
          style={styles.slider}
          minimumValue={0}
          maximumValue={1}
          value={isLoading ? 0 : value}
          onSlidingStart={onSlidingStart}
          onSlidingComplete={onSliderValueChange}
          minimumTrackTintColor="#FFFFFF"
          maximumTrackTintColor="#000000"
          thumbTintColor="#FFFFFF"
        />
      )}

      <View style={styles.controls}>
        <Text
          style={[
            styles.formattedTime,
            isLoading ? styles.hidden : undefined,
            responsiveStyles.responsiveFormattedTime,
          ]}
        >
          {formatTime(position)}
        </Text>
      </View>
    </View>
  );
};

export const AudioPlayer = forwardRef(AudioPlayerComponent);

const styles = StyleSheet.create({
  container: {
    width: '50%',
    backgroundColor: '#5B79EB',
    marginHorizontal: 'auto',
    padding: 10,
    borderRadius: 10,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  controls: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: Platform.OS === 'web' ? 20 : 0,
    marginHorizontal: Platform.OS === 'web' ? 10 : 0,
  },
  controls_disabled: {
    opacity: 0.3,
  },
  formattedTime: {
    color: 'white',
  },
  slider: {
    flex: 1,
  },
  disabledStyle: {
    opacity: 0.5,
  },
  hidden: {
    opacity: 0,
  },
});
